import { array } from 'fp-ts';
import { flow } from 'fp-ts/function';
import { isEmpty, join, omitBy, toArray } from 'lodash/fp';
import { useCallback } from 'react';
import { Customer, useAppConfiguration } from '@mews-commander/data-access';
import { CustomerClassifications, parseFlagEnumValues } from '@mews-commander/legacy-data-structures';
import { ImageResizeMode } from '@mews/enums';
import { compactArray } from '@mews/fp';
import { RouterService } from '@mews/framework';
import { createHandler } from '@mews/std';
import {
    blacklistIcon,
    cashlistIcon,
    complaintIcon,
    covidProtectionIcon,
    disabilityIcon,
    educationIcon,
    friendFamilyIcon,
    houseUseIcon,
    importantIcon,
    loyaltyIcon,
    mediaIcon,
    militaryIcon,
    paymasterIcon,
    planeIcon,
    problematicIcon,
    returningIcon,
    staffIcon,
    topManagementIcon,
    userClockIcon,
    veryImportantIcon,
} from '@optimus-web/assets';
import { IconName } from '@optimus-web/core';

const joinNames = (separator: string) => flow(compactArray, (arr) => arr.join(separator));
const joinByEmptySpace = joinNames(' ');
const joinBySpacedComma = joinNames(', ');

export function getFullName(user: Pick<Customer, 'firstName' | 'lastName'> | null | undefined) {
    return !user ? '' : joinByEmptySpace([user.firstName, user.lastName]);
}

export const getFullNameWithSecondLastName = (user: Customer | null | undefined) =>
    !user ? '' : joinByEmptySpace([user.firstName, user.lastName, user.secondLastName]);

export function getReversedFullName(user: Customer | null | undefined) {
    return !user ? '' : joinBySpacedComma([user.lastName, user.firstName]);
}

export const mapCustomerClassifications = flow(
    parseFlagEnumValues,
    array.map((value) => CustomerClassifications.getByValue(value)),
    compactArray
);

export const customerClassificationIconNameHandler = createHandler<
    number,
    { component: React.FC; iconName: IconName } | null
>(
    {
        [CustomerClassifications.PaymasterAccount.value]: { component: paymasterIcon, iconName: IconName.Paymaster },
        [CustomerClassifications.Blacklist.value]: { component: blacklistIcon, iconName: IconName.Blacklist },
        [CustomerClassifications.Media.value]: { component: mediaIcon, iconName: IconName.Media },
        [CustomerClassifications.LoyaltyProgram.value]: { component: loyaltyIcon, iconName: IconName.Loyalty },
        [CustomerClassifications.PreviousComplaint.value]: { component: complaintIcon, iconName: IconName.Complaint },
        [CustomerClassifications.Returning.value]: { component: returningIcon, iconName: IconName.Restore },
        [CustomerClassifications.Staff.value]: { component: staffIcon, iconName: IconName.Staff },
        [CustomerClassifications.FriendOrFamily.value]: {
            component: friendFamilyIcon,
            iconName: IconName.FriendFamily,
        },
        [CustomerClassifications.TopManagement.value]: {
            component: topManagementIcon,
            iconName: IconName.TopManagement,
        },
        [CustomerClassifications.Important.value]: { component: importantIcon, iconName: IconName.Important },
        [CustomerClassifications.VeryImportant.value]: {
            component: veryImportantIcon,
            iconName: IconName.VeryImportant,
        },
        [CustomerClassifications.Problematic.value]: { component: problematicIcon, iconName: IconName.Problematic },
        [CustomerClassifications.Cashlist.value]: { component: cashlistIcon, iconName: IconName.Cashlist },
        [CustomerClassifications.DisabledPerson.value]: { component: disabilityIcon, iconName: IconName.Disability },
        [CustomerClassifications.InRoom.value]: { component: houseUseIcon, iconName: IconName.HouseUse },
        [CustomerClassifications.WaitingForRoom.value]: { component: userClockIcon, iconName: IconName.UserClock },
        [CustomerClassifications.Military.value]: { component: militaryIcon, iconName: IconName.Military },
        [CustomerClassifications.Airline.value]: { component: planeIcon, iconName: IconName.Plane },
        [CustomerClassifications.HealthCompliant.value]: {
            component: covidProtectionIcon,
            iconName: IconName.CovidProtection,
        },
        [CustomerClassifications.Student.value]: {
            component: educationIcon,
            iconName: IconName.Education,
        },
    },
    null
);

export const getInlineAddress = flow(omitBy(isEmpty), toArray, join(', '));

export const useGetAvatarSrc = () => {
    const { imageBaseUrl } = useAppConfiguration();

    return useCallback(
        (imageId?: string | null) =>
            imageId ? RouterService.imageUrl(imageId, imageBaseUrl, { mode: ImageResizeMode.CoverExact }) : '',
        [imageBaseUrl]
    );
};
